import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Search, Shield, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLORS = {
  create_agency: 'bg-emerald-100 text-emerald-700',
  update_agency: 'bg-blue-100 text-blue-700',
  delete_user: 'bg-red-100 text-red-700',
  validate_fdc: 'bg-purple-100 text-purple-700',
  reject_fdc: 'bg-rose-100 text-rose-700',
  validate_sale: 'bg-purple-100 text-purple-700',
  reject_sale: 'bg-rose-100 text-rose-700',
  pay_commission: 'bg-emerald-100 text-emerald-700',
};

export const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/admin/audit-logs?limit=500`, { credentials: 'include' });
        if (r.ok) setLogs(await r.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = logs.filter((l) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      l.action?.toLowerCase().includes(q) ||
      l.actor_email?.toLowerCase().includes(q) ||
      l.entity_type?.toLowerCase().includes(q) ||
      l.entity_id?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope] flex items-center gap-2" data-testid="audit-logs-title">
            <Shield className="w-7 h-7 text-indigo-600" />
            Journal d&apos;audit
          </h1>
          <p className="text-slate-500 mt-1">Historique des actions critiques (creations, validations, paiements)</p>
        </div>
        <a
          href={`${API}/admin/audit-logs/export-csv`}
          data-testid="audit-export-csv-btn"
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 transition"
        >
          <Download className="w-4 h-4" />
          Exporter en CSV
        </a>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher (action, email, entite...)"
              className="pl-10"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="audit-search-input"
            />
          </div>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Acteur</TableHead>
                <TableHead>Entite</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Aucun log d&apos;audit</TableCell></TableRow>
              ) : (
                filtered.map((l) => (
                  <TableRow key={l.log_id} data-testid={`audit-log-${l.log_id}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                      {l.timestamp ? new Date(l.timestamp).toLocaleString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={ACTION_COLORS[l.action] || 'bg-slate-100'}>
                        {l.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{l.actor_email}</p>
                        <p className="text-xs text-slate-400">{l.actor_role}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{l.entity_type}</p>
                        <p className="text-xs text-slate-400 font-mono">{l.entity_id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <pre className="text-xs text-slate-500 max-w-xs whitespace-pre-wrap">
                        {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details, null, 1) : '-'}
                      </pre>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default AdminAuditLogs;
